// Ofertas emergentes (popups): mensajes cortos con condiciones que el equipo activa y desactiva desde el panel.
// Qué se muestra, dónde y cuándo se decide aquí con funciones puras; el navegador solo las pinta.
import { z } from "zod";

export const PAGINAS = ["inicio", "tours", "tour", "carrito", "cabanas", "restaurantes", "caballos", "bar", "eventos"] as const;
export const DISPARADORES = ["entrar", "tiempo", "salida", "carrito"] as const; // al entrar a la página, a los N segundos, al intentar irse, o con N tours en el carrito
export const FRECUENCIAS = ["sesion", "dia", "siempre"] as const;
export type Pagina = (typeof PAGINAS)[number];

const fecha = z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional();
export const entradaOferta = z.object({
  titulo: z.string().trim().min(3).max(80), texto: z.string().trim().min(3).max(240),
  boton: z.string().trim().min(2).max(30).default("Ver más"),
  enlace: z.string().trim().max(200).regex(/^(\/(?!\/)[^\s]*|https:\/\/[^\s]+)$/, "El enlace debe ser una ruta del sitio (/es/tours) o https").nullable().optional(),
  codigo: z.string().trim().toUpperCase().regex(/^[A-Z0-9]{3,24}$/).nullable().optional().or(z.literal("").transform(() => null)),
  imagen: z.string().trim().max(200).regex(/^\/(?!\/)[^\s]*$/, "La imagen debe ser una ruta del sitio").nullable().optional().or(z.literal("").transform(() => null)),
  condiciones: z.string().trim().max(400).default(""),
  activa: z.boolean().default(true),
  desde: fecha, hasta: fecha,
  paginas: z.array(z.enum(PAGINAS)).max(PAGINAS.length).default([]),
  disparador: z.enum(DISPARADORES).default("tiempo"), segundos: z.number().int().min(0).max(120).default(8),
  minimoTours: z.number().int().min(1).max(6).nullable().optional(),
  diasSemana: z.array(z.number().int().min(0).max(6)).max(7).default([]),
  frecuencia: z.enum(FRECUENCIAS).default("sesion"), prioridad: z.number().int().min(0).max(100).default(0),
}).strict().superRefine((d, c) => {
  if (d.desde && d.hasta && d.desde > d.hasta) c.addIssue({ code: "custom", message: "La fecha final es anterior a la inicial", path: ["hasta"] });
  if (d.disparador === "carrito" && !d.minimoTours) c.addIssue({ code: "custom", message: "Indica con cuántos tours en el carrito aparece", path: ["minimoTours"] });
});
export type EntradaOferta = z.infer<typeof entradaOferta>;

export type FilaOferta = { id: string; activa: boolean; desde: Date | null; hasta: Date | null; paginas: string[]; diasSemana: number[]; prioridad: number };
const iso = (d: Date) => d.toISOString().slice(0, 10);
/** Hora de México (UTC−6) para decidir «hoy» y el día de la semana. */
const hoyMx = (ahora: Date) => new Date(ahora.getTime() - 6 * 3600_000);

export function vigenteAhora(o: FilaOferta, pagina: string, ahora = new Date()) {
  if (!o.activa) return false;
  const mx = hoyMx(ahora), hoy = iso(mx);
  if (o.desde && iso(o.desde) > hoy) return false;
  if (o.hasta && iso(o.hasta) < hoy) return false;
  if (o.diasSemana.length && !o.diasSemana.includes(mx.getUTCDay())) return false;
  return o.paginas.length === 0 || o.paginas.includes(pagina);
}
export const ordenar = <T extends { prioridad: number }>(l: T[]) => [...l].sort((a, b) => b.prioridad - a.prioridad);

/** Resumen legible de las condiciones para mostrarlas al cliente (y al equipo en la lista). */
export function resumenCondiciones(o: { desde: Date | null; hasta: Date | null; diasSemana: number[]; paginas: string[]; minimoTours: number | null; condiciones: string }) {
  const dias = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"], partes: string[] = [];
  if (o.hasta) partes.push(`Vigente hasta el ${iso(o.hasta)}`);
  if (o.diasSemana.length) partes.push(`Solo ${o.diasSemana.map((d) => dias[d]).join(", ")}`);
  if (o.minimoTours) partes.push(o.minimoTours === 1 ? "Con al menos 1 tour en tu carrito" : `Con ${o.minimoTours} tours o más en tu carrito`);
  if (o.condiciones) partes.push(o.condiciones);
  return partes.join(" · ");
}

/** Reglas de frecuencia en el navegador (se pasa lo guardado). */
export function puedeMostrar(frecuencia: string, ultimaVez: number | null, ahora = Date.now(), cerradaEnSesion = false) {
  if (frecuencia === "siempre") return true;
  if (frecuencia === "sesion") return !cerradaEnSesion;
  return !ultimaVez || ahora - ultimaVez > 20 * 3600_000; // dia
}

export const datosDeOferta = (d: EntradaOferta) => ({
  titulo: d.titulo, texto: d.texto, boton: d.boton, enlace: d.enlace ?? null, codigo: d.codigo ?? null, imagen: d.imagen ?? null, condiciones: d.condiciones, activa: d.activa,
  desde: d.desde ? new Date(d.desde) : null, hasta: d.hasta ? new Date(d.hasta) : null, paginas: d.paginas, disparador: d.disparador, segundos: d.segundos,
  minimoTours: d.minimoTours ?? null, diasSemana: d.diasSemana, frecuencia: d.frecuencia, prioridad: d.prioridad,
});

